import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import {
  setDisplayCurrency,
  resetToDefault,
} from '../store/slices/currencySlice';
import {
  formatInCurrency,
  convertFromNgn,
  CURRENCIES,
  type CurrencyCode,
} from '../types/currency.types';

export function useCurrencyFormat() {
  const dispatch = useDispatch<AppDispatch>();
  const { displayCurrency, rate, adminDefault } = useSelector(
    (s: RootState) => s.currency,
  );

  const meta = CURRENCIES[displayCurrency];

  const format = useCallback(
    (amountNgn: number | null | undefined) =>
      formatInCurrency(amountNgn, displayCurrency, rate),
    [displayCurrency, rate],
  );

  const formatBoth = useCallback(
    (amountNgn: number | null | undefined) => {
      const primary = formatInCurrency(amountNgn, displayCurrency, rate);
      const alt: CurrencyCode = displayCurrency === 'NGN' ? 'USD' : 'NGN';
      const secondary = formatInCurrency(amountNgn, alt, rate);
      return `${primary} (${secondary})`;
    },
    [displayCurrency, rate],
  );

  const convert = useCallback(
    (amountNgn: number | null | undefined) =>
      convertFromNgn(Number(amountNgn ?? 0), displayCurrency, rate),
    [displayCurrency, rate],
  );

  return {
    currency: displayCurrency,
    meta,
    rate,
    defaultCurrency: adminDefault,
    hasOverride: displayCurrency !== adminDefault,
    setCurrency: (code: CurrencyCode) => dispatch(setDisplayCurrency(code)),
    resetToDefault: () => dispatch(resetToDefault()),
    format,
    formatAmount: format,
    formatBoth,
    convert,
  };
}
